<template>
  <v-app>
    <Snackbar />
    <v-navigation-drawer
      :model-value="drawer"
      @update:model-value="(val) => (drawer = val)"
      :temporary="isMobile"
      :permanent="!isMobile"
      v-if="!$route.meta.removeBanner"
      width="256"
      class="app-drawer"
    >
      <div class="text-center">
        <img :src="$store.state.config.LOGO_URL" class="logo mt-2" />
      </div>
      <v-divider class="my-2" />
      <div
        v-if="teams.length"
        class="workspace"
        data-testid="workspace-switcher"
      >
        <v-menu location="bottom start">
          <template #activator="{ props }">
            <button type="button" class="workspace__current" v-bind="props">
              <span class="workspace__name">{{ currentWorkspace.name }}</span>
              <span class="workspace__role">{{
                roleLabel(currentWorkspace.role)
              }}</span>
              <v-icon size="small">mdi-unfold-more-horizontal</v-icon>
            </button>
          </template>
          <v-list density="compact">
            <v-list-item
              v-for="workspace in workspaces"
              :key="workspace.id"
              :active="workspace.id === currentWorkspace.id"
              @click="selectWorkspace(workspace.id)"
            >
              <v-list-item-title>{{ workspace.name }}</v-list-item-title>
              <v-list-item-subtitle>{{
                roleLabel(workspace.role)
              }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <v-list-item
        to="/"
        :disabled="isEmailVerified2022 === false"
        prepend-icon="mdi-view-dashboard"
      >
        <v-list-item-title> Dashboard </v-list-item-title>
      </v-list-item>

      <v-list-item
        to="/links"
        :disabled="isEmailVerified2022 === false"
        prepend-icon="mdi-link"
      >
        <v-list-item-title>Links</v-list-item-title>
      </v-list-item>

      <v-tooltip location="right">
        <template v-slot:activator="{ props }">
          <v-list-item
            v-bind="props"
            to="/deep-linking"
            :disabled="isEmailVerified2022 === false"
            prepend-icon="mdi-cellphone-cog"
          >
            <v-list-item-title>Deep Linking Apps</v-list-item-title>
          </v-list-item>
        </template>
        <span>Configure supported apps</span>
      </v-tooltip>

      <v-list-item
        to="/domains"
        :disabled="isEmailVerified2022 === false"
        prepend-icon="mdi-earth"
      >
        <v-list-item-title>Domains</v-list-item-title>
      </v-list-item>

      <v-list-item
        to="/integrations"
        :disabled="isEmailVerified2022 === false"
        prepend-icon="mdi-cable-data"
      >
        <v-list-item-title>
          Integrations
          <v-chip
            size="x-small"
            class="ml-1"
            style="position: absolute; top: 7px"
            >MCP</v-chip
          >
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        to="/team"
        :disabled="isEmailVerified2022 === false"
        prepend-icon="mdi-account-multiple"
      >
        <v-list-item-title>Team</v-list-item-title>
      </v-list-item>

      <v-list-item
        to="/plan"
        :disabled="isEmailVerified2022 === false"
        prepend-icon="mdi-wallet"
      >
        <v-list-item-title>Plan</v-list-item-title>
      </v-list-item>

      <v-list-item
        to="/account"
        :disabled="isEmailVerified2022 === false"
        prepend-icon="mdi-account"
      >
        <v-list-item-title>Account</v-list-item-title>
      </v-list-item>

      <v-divider />
      <v-list-item
        @click="logout"
        id="logout-btn"
        prepend-icon="mdi-exit-to-app"
      >
        <v-list-item-title>Logout</v-list-item-title>
      </v-list-item>
    </v-navigation-drawer>

    <v-app-bar
      flat
      color="white"
      v-if="!$route.meta.removeBanner && isMobile"
      height="64"
      :absolute="false"
      style="position: relative"
    >
      <v-app-bar-nav-icon @click.stop="toggleDrawer"></v-app-bar-nav-icon>
      <v-spacer></v-spacer>
      <!-- Add any other app bar content here -->
    </v-app-bar>

    <v-main>
      <!-- close-on-back: v-overlay defaults to hijacking the back button so a
           dialog closes instead of navigating. The router guard raises this
           overlay at the *start* of every navigation, so on a popstate the
           overlay is already active when vuetify's own guard runs and it
           aborts the navigation (and, being persistent, never closes). -->
      <v-overlay
        scrim="#ffffff"
        persistent
        :close-on-back="false"
        class="app-loading-overlay align-center justify-center"
        :model-value="$store.state.isLoading"
        opacity="1"
        :z-index="100"
      >
        <v-progress-circular
          indeterminate
          color="blue-lighten-4"
          size="64"
        ></v-progress-circular>
        <p class="mt-5 text-grey">Loading</p>
      </v-overlay>
      <div
        v-if="
          isEmailVerified2022 === false &&
          $route.name !== 'verify-email' &&
          $route.name !== 'almost-done'
        "
        class="ln-verify-banner"
        role="alert"
      >
        <div class="ln-verify-banner__inner">
          <v-icon size="small" class="ln-verify-banner__icon"
            >mdi-email-alert</v-icon
          >
          <div class="ln-verify-banner__text">
            <strong>Please verify your new email</strong>
            <span>
              We sent a verification link to <em>{{ getUserEmail }}</em
              >. Navigation is disabled until verified. Check inbox (and spam).
            </span>
          </div>
          <div class="ln-verify-banner__actions">
            <v-btn
              size="small"
              variant="flat"
              color="primary"
              :loading="resendingVerification"
              @click="resendVerification"
            >
              Resend email
            </v-btn>
            <v-btn
              size="small"
              variant="text"
              @click="refreshVerificationStatus"
            >
              I've verified
            </v-btn>
            <v-btn size="small" variant="text" to="/verify-email">
              Open verification page
            </v-btn>
          </div>
        </div>
      </div>
      <router-view></router-view>
    </v-main>

    <v-footer class="text-body-medium flex-wrap">
      <div>
        <v-btn
          size="small"
          variant="text"
          class="text-grey-darken-3"
          :href="'mailto:' + $store.state.config.SUPPORT_EMAIL"
          >Support</v-btn
        >
        <v-btn
          variant="text"
          size="small"
          class="text-grey-darken-3"
          href="/#/privacy-policy"
          target="_blank"
          >Privacy</v-btn
        >
        <v-btn
          variant="text"
          size="small"
          class="text-grey-darken-3"
          href="/#/terms-of-service"
          target="_blank"
          >Terms</v-btn
        >
      </div>
      <v-spacer></v-spacer>
      <div class="footer-meta">
        2017 - {{ new Date().getFullYear() }} &copy; 1LINK.IO
      </div>
    </v-footer>
  </v-app>
</template>

<script>
import { mapGetters } from "vuex";
import Snackbar from "./components/Snackbar.vue";
import config from "./config";
import { auth, logout, sendEmailVerification } from "./auth";
import {
  ROLE_LABELS,
  getSelectedTeamId,
  switchWorkspace,
  takeTeamUnavailableNotice,
} from "./workspace";

export default {
  name: "App",
  components: {
    Snackbar,
  },
  data() {
    const isMobile = window.innerWidth < 1264;
    return {
      drawer: !isMobile, // true for desktop, false for mobile by default
      isMobile: isMobile,
      resendingVerification: false,
    };
  },
  computed: {
    ...mapGetters([
      "isEmailVerified2022",
      "isEmailVerified",
      "getUserEmail",
      "teams",
    ]),
    /** the person's own account first, then their teams, last joined first */
    workspaces() {
      const own = {
        id: this.$store.state.user?.uid,
        name: "Personal",
        role: "admin",
      };
      return [own, ...this.teams];
    },
    /** not reactive to the storage on purpose: a change of workspace reloads the page */
    currentWorkspace() {
      const selected = getSelectedTeamId(this.$store.state.user?.uid);
      return (
        this.workspaces.find((w) => w.id === selected) || this.workspaces[0]
      );
    },
  },
  methods: {
    async resendVerification() {
      if (this.resendingVerification) return;
      if (!auth().currentUser) {
        this.$store.commit("showSnackbarMessage", {
          content: "Please sign in again.",
          color: "error",
        });
        return;
      }
      this.resendingVerification = true;
      try {
        await sendEmailVerification();
        this.$store.commit("showSnackbarMessage", {
          content: "Verification email sent to " + this.getUserEmail,
          color: "success",
        });
      } catch (err) {
        console.error("failed to resend verification", err);
        const content =
          err && err.code === "auth/too-many-requests"
            ? "Too many requests, please wait a few minutes before trying again."
            : (err && err.message) ||
              "Failed to send verification email, please try again.";
        this.$store.commit("showSnackbarMessage", { content, color: "error" });
      } finally {
        this.resendingVerification = false;
      }
    },
    async refreshVerificationStatus() {
      try {
        const cur = auth().currentUser;
        if (!cur) return;
        await cur.reload();
        const updated = auth().currentUser;
        this.$store.commit("setUser", updated);
        if (updated.emailVerified) {
          this.$store.commit("showSnackbarMessage", {
            content: "Email verified! Navigation unlocked.",
            color: "success",
          });
        } else {
          this.$store.commit("showSnackbarMessage", {
            content:
              "Not yet verified — please click the link in your email and try again.",
            color: "info",
          });
        }
      } catch (err) {
        console.error("failed to refresh verification status", err);
        this.$store.commit("showSnackbarMessage", {
          content: "Failed to refresh status, please try again.",
          color: "error",
        });
      }
    },
    async logout() {
      await logout();
      window.location.href = config.WEBSITE_URL;
    },

    onResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 1264;

      // Only auto-close/open when switching between mobile/desktop
      if (wasMobile !== this.isMobile) {
        this.drawer = !this.isMobile;
      }
    },
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    roleLabel(role) {
      return ROLE_LABELS[role] || "";
    },
    selectWorkspace(id) {
      if (id === this.currentWorkspace.id) {
        return;
      }
      switchWorkspace(this.$store.state.user.uid, id);
    },
  },
  mounted() {
    this.onResize(); // Set initial state based on screen size
    window.addEventListener("resize", this.onResize);
    if (takeTeamUnavailableNotice()) {
      this.$store.commit("showSnackbarMessage", {
        content:
          "You no longer have access to that team, so you are back in your personal workspace.",
        color: "info",
      });
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
  },
};
</script>

<style scoped>
.v-application :deep(.v-application__wrap) {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-drawer {
  border-right: 1px solid var(--ln-line);
  box-shadow: none;
  background: var(--ln-surface);
  padding-top: 0 !important;
}

/* the permanent (desktop) drawer is pinned low so app overlays paint over it.
   the temporary (mobile) one must keep vuetify's own stacking instead: it
   renders a scrim at z-index 1005 and itself at 1006, so forcing 5 here put
   the drawer *under* its own scrim, dimming it and eating every click. */
.app-drawer:not(.v-navigation-drawer--temporary) {
  z-index: 5 !important;
}

.v-app-bar .v-btn--icon {
  color: var(--ln-muted);
}

.v-app-bar {
  border-bottom: 1px solid var(--ln-line) !important;
  box-shadow: none !important;
}

.app-drawer :deep(.v-divider) {
  border-color: var(--ln-line);
  opacity: 1;
}

.app-drawer :deep(.v-list-item) {
  min-height: 44px;
}

.app-drawer :deep(.v-list-item-title) {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  color: var(--ln-ink-2);
}

.app-drawer :deep(.v-list-item .v-icon) {
  color: var(--ln-muted);
  font-size: 20px;
}

/* vuetify paints the active row with a primary-tinted ::before overlay;
   swap it for a flat surface plus a solid left rule */
.app-drawer :deep(.v-list-item--active > .v-list-item__overlay) {
  opacity: 0 !important;
}

.app-drawer :deep(.v-list-item--active) {
  background: var(--ln-blue-bg);
  box-shadow: inset 2px 0 0 var(--ln-accent);
}

.app-drawer :deep(.v-list-item--active .v-list-item-title) {
  color: var(--ln-accent);
  font-weight: 500;
}

.app-drawer :deep(.v-list-item--active .v-icon) {
  color: var(--ln-accent);
}

.workspace {
  padding: 0 12px 8px;
}

.workspace__current {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--ln-line);
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.workspace__current:hover {
  background: rgba(127, 127, 127, 0.08);
}

.workspace__name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace__role {
  font-size: 12px;
}

.new-pill {
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--ln-yellow-bg);
  color: var(--ln-yellow-fg);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* the loading overlay must cover the page from its first frame: vuetify
   fades the scrim and spinner in from opacity 0 (fade-transition, appear),
   so the view underneath showed through for ~0.3s on every load. The leave
   fade is kept, so the page still fades in once its data is ready */
.app-loading-overlay :deep(.v-overlay__scrim.fade-transition-enter-from),
.app-loading-overlay :deep(.v-overlay__scrim.fade-transition-enter-active),
.app-loading-overlay :deep(.v-overlay__content.fade-transition-enter-from),
.app-loading-overlay :deep(.v-overlay__content.fade-transition-enter-active) {
  opacity: 1;
  transition: none;
}

.v-navigation-drawer {
  padding-top: 0 !important;
}

.v-main {
  transition: none !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  min-height: 100vh;
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.logo {
  height: 40px;
  width: auto;
  max-height: none;
  margin: 8px 0 0;
}

.v-navigation-drawer:not(.v-navigation-drawer--right) ~ .v-main {
  margin-left: 256px !important;
  width: calc(100% - 256px) !important;
}

footer {
  padding: 24px;
  width: 100%;
  margin-top: auto;
  background-color: #f5f5f5;
  border-top: 1px solid var(--ln-line);
}

footer .v-btn {
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.footer-meta {
  font-family: var(--ln-mono);
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--ln-muted);
}

.ln-verify-banner {
  background: #fff7e6;
  border-bottom: 1px solid #f5d79a;
  padding: 12px 16px;
}

.ln-verify-banner__inner {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
}

.ln-verify-banner__icon.v-icon {
  color: #b7791f;
}

.ln-verify-banner__text {
  flex: 1 1 240px;
  min-width: 200px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--ln-ink-2);
}

.ln-verify-banner__text strong {
  display: block;
  font-size: 13px;
  color: var(--ln-ink);
}

.ln-verify-banner__text em {
  font-style: normal;
  font-weight: 500;
  color: var(--ln-ink);
  word-break: break-all;
}

.ln-verify-banner__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

@media (max-width: 1263px) {
  .v-navigation-drawer:not(.v-navigation-drawer--right) ~ .v-main {
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
  }

  .v-navigation-drawer {
    margin-top: 64px;
    height: calc(100% - 64px) !important;
    position: fixed !important;
    transition: transform 0.3s ease-in-out !important;
  }
}
</style>
